/**
 * Notifications to the phone (docs/refactor/28): once the person allows notifications in a browser, it subscribes to
 * the server's pushes, so work sent from the phone says when it ends, fails or needs them — with the page closed. The
 * desktop app has its own native notifications and does not subscribe. On an iPhone, only the site added to the home
 * screen can receive them (Apple's rule).
 */

import { api } from './api'
import { isDesktop } from './desktop'

/** The key as the Push API wants it: bytes from base64url. */
function keyBytes(base64url: string): Uint8Array {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4)
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
}

/** Whether this browser can receive pushes at all. */
export function pushPossible(): boolean {
  return !isDesktop() && typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** An iPhone or iPad browsing the site, not the app on the home screen: pushes need it added there first. */
export function needsHomeScreen(): boolean {
  if (typeof navigator === 'undefined') return false
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true
  return ios && !standalone
}

/**
 * Subscribes this browser when notifications are allowed and the server has push set up. Safe to call often: an
 * existing subscription is sent again (the server keeps one per device). Returns whether this device is subscribed.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!pushPossible() || Notification.permission !== 'granted') return false
  try {
    const { publicKey } = await api.push.key()
    if (!publicKey) return false
    const registration = await navigator.serviceWorker.register('/sw.js')
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource }))
    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
    await api.push.subscribe({ endpoint: json.endpoint ?? '', keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' } })
    return true
  } catch {
    // Push refused by the browser, or the server is not set up: the page's own notifications still work while it is open.
    return false
  }
}
